import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { CSVLink } from "react-csv";
import currency from "currency.js";
import moment from "moment";
import { toast } from "react-toastify";
import { Dropdown, Form, Row } from "react-bootstrap";
import { ExcelIcon, ExportIcon } from "../icons";
import { useDownloadExcel } from "../useDownloadExcel";
import { URLS } from "../urls";
import ModalLoader from "../ModalLoader";
import TableComponent from "../TableComponent";
import {
  arrangeEventObject,
  fetchActionsUtil,
  stringifyQueryString,
} from "../../utils";
import NoTableItem from "../NoTableItem";
import { SingleDatePicker } from "../SingleDatePicker";
import CustomPagination from "../Paginations";
import { UseQueryData } from "../UseQueryData";

export function PayeeScheduleComponent({ branch_id, SwitchCompanyComponent }) {
  // ------------------ Filter Search -------------------//
  const initialQueryParams = {
    limit: 20,
    page: 1,
    employeeType: "All",
    category: "",
    fullAndFinally: "",
    startDate: "",
    endDate: "",
  };
  const { queryParams, theQueryParams, onChangeInput, setQueryParams } =
    UseQueryData({
      initialQueryParams,
      timer: 1500,
    });

  const [returnParams, setReturnParams] = useState({
    count: 0,
    endIndex: 0,
    startIndex: 0,
  });

  // ------------------ Filter Search -------------------//

  const [payroll, setPayroll] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  //-------------- fetch excel hook
  const [excelData, setExcelData] = useState([]);
  const CSVLinkRef = useRef(null);
  const [isfetchingExcel] = useDownloadExcel(excelData, CSVLinkRef);
  //-------------- fetch excel hook

  // Fetch job categories on component mount
  const fetchAll = useCallback(async () => {
    try {
      const categoryData = fetchActionsUtil(
        `${URLS.backendJobCategory}/get/${branch_id}`,
        "GET"
      );
      const [categoryResult] = await Promise.all([categoryData]);

      setCategories(categoryResult?.jobCategories || []);
    } catch (error) {
      console.error("Error fetching job categories:", error);
    }
  }, [branch_id]);

  useEffect(() => {
    let isMounted = true;
    // Fetch job categories when the component mounts
    if (isMounted) {
      fetchAll();
    }
    // Cleanup function to avoid memory leaks
    return () => {
      setCategories([]);
      isMounted = false;
    };
  }, [fetchAll]);

  const getPayroll = useCallback(async () => {
    setIsLoading(true);

    try {
      // Parallel requests
      const [payrollData] = await Promise.all([
        fetchActionsUtil(
          `${URLS.backendPayroll}/get-payee-report?${stringifyQueryString({
            ...queryParams,
            branch_id,
          })}`
        ),
      ]);

      // Handle empty payroll
      const payroll = payrollData.payroll || [];
      if (!payroll.length) {
        setPayroll([]);
        return;
      }
      setReturnParams({
        count: payrollData.count,
        endIndex: payrollData.endIndex,
        startIndex: payrollData.startIndex,
      });
      setPayroll(payroll);
    } catch (err) {
      console.error("Payroll processing failed:", err);
    } finally {
      setIsLoading(false);
    }
  }, [branch_id, queryParams]);

  useEffect(() => {
    let isMounted = true; // Track component mount status

    if (isMounted) {
      getPayroll();
    }
    return () => {
      isMounted = false; // Cleanup on unmount
    };
  }, [getPayroll]);

  const monthArray = useMemo(() => {
    const startDate = theQueryParams.startDate
      ? moment(theQueryParams.startDate, "MM/YYYY")
      : "";

    const endDate = theQueryParams.endDate
      ? moment(theQueryParams.endDate, "MM/YYYY")
      : "";

    const monthArray = [];
    const current = moment(startDate);
    while (current.isSameOrBefore(endDate, "month")) {
      monthArray.push({
        yearMonth: current.format("MMMM YYYY"),
        date: current.format("MM/YYYY"),
      }); // "January 2023"
      current.add(1, "month");
    }

    return monthArray;
  }, [theQueryParams]);

  const onDownloadExcelData = async () => {
    if (!payroll?.length > 0) return toast.error("Nothing to download!!!");

    const currencyCell = (val) => currency(val || 0, { symbol: "" }).format();

    const normalCell = (val) => val || "";

    let exData = payroll.map(
      (
        { personalInfo, employeeId, jobGrade, bankDetails, payrollItems },
        index
      ) => [
        index + 1,
        `${personalInfo?.name.first || ""} ${
          personalInfo?.name?.middle || ""
        } ${personalInfo?.name?.last || ""}`.trim(),
        employeeId,
        jobGrade,
        normalCell(personalInfo?.nationality || ""),
        normalCell(personalInfo?.address?.state || ""),
        normalCell(bankDetails?.accountNumber || ""),
        normalCell(personalInfo?.birth || ""),
        normalCell(personalInfo?.BVN || ""),
        normalCell(personalInfo?.phone?.home || ""),
        normalCell(personalInfo?.taxID || ""),
        ...monthArray
          .map(({ date }, i) => {
            const data =
              payrollItems?.find(
                (s) =>
                  `${s?.payrollMonth?.padStart(2, "0")}/${s?.payrollYear}` ===
                  date
              ) || {};
            return [
              currencyCell(data?.salaryTotal),
              currencyCell(data?.taxTotal),
            ];
          })
          .flat(),
      ]
    );

    const normalHead = [
      "No",
      "Employee Name",
      "Employee ID",
      "Division",
      "Nationality",
      "State",
      "Account Number",
      "Date of birth",
      "BVN",
      "Mobile Number",
      "Tax Payer ID",
      ...monthArray
        ?.map((el, i) => [
          `Gross Salary ${el?.yearMonth}`,
          `Tax ${el?.yearMonth}`,
        ])
        .flat(),
    ];

    const date = `Date Prepared: ${moment(new Date()).format(
      "MMMM DD yyyy k:mm:ss z"
    )}`;

    exData = [
      ["Payee Schedule Report"],
      [date],
      [""],
      normalHead,
      ...exData,
      [""],
    ];

    // console.log(exData);
    setExcelData(exData);
  };

  const filterByDateRange = (event, name) => {
    onChangeInput(
      arrangeEventObject({
        name: [name],
        value: moment(event).format("MM/YYYY"),
      })
    );
  };

  const validationEndDate = (currentDate) => {
    return currentDate.isAfter(
      moment(theQueryParams?.startDate, "MM/YYYY").add(0, "months")
    );
  };

  // Table head function
  const tableHead = () => {
    if (!payroll?.length) return null;
    return (
      <thead className="position-sticky top-0">
        <tr>
          <th>No</th>
          <th>Employee Name</th>
          <th>Employee ID</th>
          <th>Division</th>
          <th>Nationality</th>
          <th>State</th>
          <th>Account Number</th>
          <th>Date of birth</th>
          <th>BVN</th>
          <th>Mobile Number</th>
          <th>Tax Payer ID</th>
          {monthArray?.map((el, i) => (
            <>
              <th key={`custom-${i}`}>{`Gross Salary ${el.yearMonth}`}</th>
              <th key={`custom-${i + 1}`}>{`Tax ${el.yearMonth}`}</th>
            </>
          ))}
        </tr>
      </thead>
    );
  };

  // Table body data function
  const tableBodyData = (employee, index) => {
    const { personalInfo, employeeId, jobGrade, bankDetails, payrollItems } =
      employee;

    const name = `${personalInfo?.name?.first ?? ""} ${
      personalInfo?.name?.middle ?? ""
    } ${personalInfo?.name?.last ?? ""} `.trim();

    const currencyCell = (val) => (
      <td>{currency(val || 0, { symbol: "" }).format()}</td>
    );

    const normalCell = (val) => <td>{val || ""}</td>;

    const payrollMap =
      payrollItems?.reduce((acc, item) => {
        const paddedMonth = item.payrollMonth.padStart(2, "0");
        const key = `${paddedMonth}/${item.payrollYear}`;
        acc[key] = item;
        return acc;
      }, {}) || {};

    return (
      <>
        <td>{returnParams.startIndex + index + 1}</td>
        <td>{name.trim()}</td>
        <td>{employeeId}</td>
        <td>{jobGrade}</td>
        {normalCell(personalInfo?.nationality || "")}
        {normalCell(personalInfo?.address?.state || "")}
        {normalCell(bankDetails?.accountNumber || "")}
        {normalCell(personalInfo?.birth || "")}
        {normalCell(personalInfo?.BVN || "")}
        {normalCell(personalInfo?.phone?.home || "")}
        {normalCell(personalInfo?.taxID || "")}
        {monthArray.map(({ date }, i) => {
          const data = payrollMap[date] || {};
          return (
            <React.Fragment key={i}>
              {currencyCell(data.salaryTotal || 0)}
              {currencyCell(data.taxTotal || 0)}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const dropDownFunction = (qty) => {
    setQueryParams((el) => ({ ...el, limit: qty }));
  };

  return (
    <div>
      <div className="d-flex justify-content-between my-4">
        <div className="d-flex">
          <div className="d-flex align-items-center">
            <label>Start Date</label>
            <div className="mx-2">
              <SingleDatePicker
                value={theQueryParams.startDate}
                filterByDateRange={filterByDateRange}
                name="startDate"
                dateFormat="MM/YYYY"
              />
            </div>
          </div>

          <div className="d-flex align-items-center">
            <label>End Date</label>
            <div className="mx-2">
              <SingleDatePicker
                value={theQueryParams.endDate}
                filterByDateRange={filterByDateRange}
                name="endDate"
                dateFormat="MM/YYYY"
                isValidDate={validationEndDate}
              />
            </div>
          </div>
        </div>

        <div className="d-flex">
          <select
            className="form-control mx-2"
            aria-label="Default select example"
            value={theQueryParams.employeeType}
            name="employeeType"
            onChange={onChangeInput}
          >
            <option value="All">All Employee</option>
            <option value="staff">Staff</option>
            <option value="contract">Contract</option>
          </select>
          <div className="mx-3 w-100">
            <Form.Control
              as="select"
              value={theQueryParams.category}
              onChange={onChangeInput}
              name="category"
            >
              <option value="">All Categories</option>
              {categories && categories.length
                ? categories.map((category) => {
                    return (
                      <option value={category._id}>{category.name}</option>
                    );
                  })
                : null}
            </Form.Control>
          </div>
          <div className="mx-2 w-100">
            <select
              className="form-control mx-2"
              aria-label="Default select example"
              name="fullAndFinally"
              value={theQueryParams.fullAndFinally}
              onChange={onChangeInput}
            >
              <option value="">Normal Payroll</option>
              <option value={true}>FullAndFinally</option>
            </select>
          </div>
          <div className="mx-4">
            <CSVLink
              className="btn print d-none"
              filename={`Payee Schedule Report (${moment(new Date()).format(
                "DD-MMM-yyyy hh:mm:ss a"
              )}).csv`}
              data={excelData}
              ref={CSVLinkRef}
            />

            <Dropdown>
              <Dropdown.Toggle
                variant=""
                className="print"
                disabled={isfetchingExcel}
                bsPrefix=""
              >
                <span className="mx-2">Export</span>
                <ExportIcon color="#008000" />
              </Dropdown.Toggle>
              <Dropdown.Menu
                popperConfig={{
                  strategy: "fixed",
                }}
                renderOnMount
                className="text-center"
              >
                <Dropdown.Item
                  as="div"
                  onClick={onDownloadExcelData}
                  className="p-cursor"
                >
                  Excel <ExcelIcon color="#008000" />
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>
      {SwitchCompanyComponent ? (
        <div>
          <SwitchCompanyComponent />
        </div>
      ) : null}
      <div>
        {payroll.length ? (
          <Row className="table-height">
            <TableComponent
              responsive
              striped
              sticky
              tableHeadsFunction={tableHead}
              mainDataArray={payroll}
              tableDataRowFunction={tableBodyData}
              className="product-table text-nowrap"
            />
          </Row>
        ) : (
          <NoTableItem title="No Payee Data Found for the selected criteria" />
        )}
      </div>
      <div className="my-4">
        <CustomPagination
          setQueryParams={setQueryParams}
          startIndex={returnParams?.startIndex}
          endIndex={returnParams?.endIndex}
          count={returnParams?.count}
          limit={queryParams?.limit}
          page={queryParams?.page}
          dropDownFunction={dropDownFunction}
        />
      </div>
      <ModalLoader show={isLoading} />
    </div>
  );
}
